import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

/**
 * shadcn/ui style: the component lives in your repo, so change it here rather
 * than fighting a dependency two years from now.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none",
  {
    variants: {
      // Filled buttons go flat grey when disabled rather than fading: a
      // half-transparent ink button still reads as the primary action, just
      // washed out. Outline and ghost have no fill to swap, so they fade.
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/85 disabled:bg-muted disabled:text-muted-foreground",
        outline: "border border-border bg-transparent hover:bg-muted disabled:opacity-50",
        ghost: "text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 disabled:bg-muted disabled:text-muted-foreground",
      },
      size: {
        // 44px on mobile (touch-target guidance), back to the original 36px
        // at `md`+ where a mouse/trackpad is the norm.
        default: "h-11 px-4 py-2 md:h-9",
        sm: "h-8 rounded-md px-3 text-xs",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { buttonVariants };
